import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Checkbox, IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function ProjectTable({
  name,
  rows,
  activeFilter,
  clients,
  projecttypes,
  projectsubtypes,
  employees,
  selectedDates,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  onUpdateDropDownData,
  resetReportingState,
  getClients,
  getEmployees,
  getProjectTypes,
  getProjectSubTypes,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    ProjectId: '',
    ProjectName: '',
    DimClientKey: 'All',
    DimProjectTypeKey: 'All',
    DimProjectSubTypeKey: 'All',
    DimEmployeeDirectorKey: 'All',
    DimEmployeeProjectManagerKey: 'All',
    DimEmployeeSupervisorKey: 'All',
    DimEmployeeProjectSourceOneKey: 'All',
    DimEmployeeProjectSourceTwoKey: 'All',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'ProjectId':
      case 'ProjectName':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'DimClientKey':
        return (a, b) => {
          const aName = getClientNameById(a[sortColumn]);
          const bName = getClientNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimProjectTypeKey':
        return (a, b) => {
          const aName = getProjectTypeNameById(a[sortColumn]);
          const bName = getProjectTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
     
      case 'DimProjectSubTypeKey':
        return (a, b) => {
          const aName = getProjectSubTypeNameById(a[sortColumn]);
          const bName = getProjectSubTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimEmployeeDirectorKey':
      case 'DimEmployeeProjectManagerKey':
      case 'DimEmployeeSupervisorKey':
      case 'DimEmployeeProjectSourceOneKey':
      case 'DimEmployeeProjectSourceTwoKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    if (activeFilter) {
      setTableRows(rows.filter((row) => row.IsActive === true));
    } else {
      setTableRows(rows);
    }

    return () => {
      setTableRows([]);
    };
  }, [rows, activeFilter]);

  const getClientNameById = (id) => {
    if (!id) return '';
    const client = clients?.find(client => client[0] === id);
    return client ? client[1] : '';
  };

  const getProjectTypeNameById = (id) => {
    if (!id) return '';
    const projectType = projecttypes?.find(type => type[0] === id);
    return projectType ? projectType[1] : '';
  };

  const getProjectSubTypeNameById = (id) => {
    if (!id) return '';
    const projectSubType = projectsubtypes?.find(type => type[0] === id);
    return projectSubType ? projectSubType[1] : '';
  };

  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(emp => emp[0] === id);
    return employee ? employee[1] : '';
  };  

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'ProjectId',
      'DimClientKey',
      'DimProjectTypeKey',
      'DimProjectSubTypeKey',
      'DimEmployeeDirectorKey',
      'DimEmployeeProjectManagerKey',
      'DimEmployeeSupervisorKey',
      'DimEmployeeProjectSourceOneKey',
      'DimEmployeeProjectSourceTwoKey',
      'IsActive',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('ProjectId') && item.hasOwnProperty('ProjectName') && item.hasOwnProperty('DimClientKey')
        && item.hasOwnProperty('DimProjectTypeKey') && item.hasOwnProperty('DimProjectSubTypeKey')
        && item.hasOwnProperty('DimEmployeeDirectorKey') && item.hasOwnProperty('DimEmployeeProjectManagerKey')
        && item.hasOwnProperty('DimEmployeeSupervisorKey') && item.hasOwnProperty('DimEmployeeProjectSourceOneKey')
        && item.hasOwnProperty('DimEmployeeProjectSourceTwoKey');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain ProjectId, ProjectName, DimClientKey, DimProjectTypeKey, DimProjectSubTypeKey, DimEmployeeDirectorKey, DimEmployeeProjectManagerKey, DimEmployeeSupervisorKey, DimEmployeeProjectSourceOneKey, and DimEmployeeProjectSourceTwoKey.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getClients();
        getEmployees();
        getProjectTypes();
        getProjectSubTypes();
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (field.column.key === 'ProjectId') {
      let new_project_id = updated_data.updated[updated_data.cellKey];

      if (rows.find((row) => row.ProjectId === new_project_id)) {
        toast.warning(
          'You cannot insert the duplicate the ProjectId.',
        );
        return;
      }
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 200) {
          onUpdateDropDownData(name);
          resetReportingState();
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const projectColumns = useMemo(() => {
    let project_columns = [
      SelectColumn,
      {
        key: 'ProjectId',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ID</b>
            {frozenColumns.indexOf('ProjectId') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectId')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectId')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProjectId')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'ProjectId')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ProjectId}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProjectId: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ProjectId') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'ProjectName',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ProjectName</b>
            {frozenColumns.indexOf('ProjectName') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectName')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectName')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProjectName')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'ProjectName')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ProjectName}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProjectName: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ProjectName') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'DimClientKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client</b>
            {frozenColumns.indexOf('DimClientKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimClientKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimClientKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimClientKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimClientKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimClientKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'block' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimClientKey ? props.row.DimClientKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimClientKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {clients &&
                clients.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Type</b>
            {frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimProjectTypeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimProjectTypeKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projecttypes &&
                  projecttypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectTypeKey ? props.row.DimProjectTypeKey : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projecttypes &&
                projecttypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectSubTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>SubType</b>
            {frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimProjectSubTypeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimProjectSubTypeKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectSubTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectSubTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projectsubtypes &&
                  projectsubtypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectSubTypeKey
                  ? props.row.DimProjectSubTypeKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectSubTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projectsubtypes &&
                projectsubtypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeDirectorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Director</b>
            {frozenColumns.indexOf('DimEmployeeDirectorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeDirectorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeDirectorKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeDirectorKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeDirectorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeDirectorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeDirectorKey
                  ? props.row.DimEmployeeDirectorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeDirectorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectManagerKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Manager</b>
            {frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectManagerKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeProjectManagerKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectManagerKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectManagerKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectManagerKey
                  ? props.row.DimEmployeeProjectManagerKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectManagerKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeSupervisorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Associate</b>
            {frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeSupervisorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeSupervisorKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeSupervisorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeSupervisorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeSupervisorKey
                  ? props.row.DimEmployeeSupervisorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeSupervisorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectSourceOneKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 1</b>
            {frozenColumns.indexOf('DimEmployeeProjectSourceOneKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceOneKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceOneKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectSourceOneKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeProjectSourceOneKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectSourceOneKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectSourceOneKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectSourceOneKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectSourceOneKey
                  ? props.row.DimEmployeeProjectSourceOneKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectSourceOneKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectSourceTwoKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 2</b>
            {frozenColumns.indexOf('DimEmployeeProjectSourceTwoKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceTwoKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectSourceTwoKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectSourceTwoKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeProjectSourceTwoKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectSourceTwoKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectSourceTwoKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectSourceTwoKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectSourceTwoKey
                  ? props.row.DimEmployeeProjectSourceTwoKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectSourceTwoKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'IsActive',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Active</b>
            {frozenColumns.indexOf('IsActive') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsActive') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive ? props.row.IsActive : false}
              onChange={(e) => {
                props.onRowChange(
                  {
                    ...props.row,
                    IsActive: e.target.checked,
                  },
                  true,
                );
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    let frozenCols = project_columns.filter((col) => col.frozen === true);
    let unfrozenCols = project_columns.filter((col) => col.frozen === false);
    project_columns = [...frozenCols, ...unfrozenCols];

    return project_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    clients,
    employees,
    projecttypes,
    projectsubtypes,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.ProjectId
          ? r.ProjectId?.toLowerCase().includes(filters.ProjectId.toLowerCase())
          : true) &&
        (filters.ProjectName
          ? r.ProjectName?.toLowerCase().includes(
              filters.ProjectName.toLowerCase(),
            )
          : true) &&
        (filters.DimClientKey !== 'All'
          ? // eslint-disable-next-line
            r.DimClientKey == filters.DimClientKey
          : true) &&
        (filters.DimProjectTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectTypeKey == filters.DimProjectTypeKey
          : true) &&
        (filters.DimProjectSubTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectSubTypeKey == filters.DimProjectSubTypeKey
          : true) &&
        (filters.DimEmployeeDirectorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeDirectorKey == filters.DimEmployeeDirectorKey
          : true) &&
        (filters.DimEmployeeProjectManagerKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectManagerKey ==
            filters.DimEmployeeProjectManagerKey
          : true) &&
        (filters.DimEmployeeSupervisorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeSupervisorKey == filters.DimEmployeeSupervisorKey
          : true) &&
        (filters.DimEmployeeProjectSourceOneKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectSourceOneKey ==
            filters.DimEmployeeProjectSourceOneKey
          : true) &&
        (filters.DimEmployeeProjectSourceTwoKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectSourceTwoKey ==
            filters.DimEmployeeProjectSourceTwoKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      Project: {
        columns: [],
        data: [],
      },
      Client: {
        columns: [],
        data: [],
      },
      'Project Type': {
        columns: [],
        data: [],
      },
      'Project Sub Type': {
        columns: [],
        data: [],
      },
      Employee: {
        columns: [],
        data: [],
      },
    };

    export_value.Project.columns = [
      { title: 'ProjectId', width: { wpx: 80 } },
      { title: 'ProjectName', width: { wpx: 80 } },
      { title: 'DimClientKey', width: { wpx: 80 } },
      { title: 'DimProjectTypeKey', width: { wpx: 80 } },
      { title: 'DimProjectSubTypeKey', width: { wpx: 80 } },
      { title: 'DimEmployeeDirectorKey', width: { wpx: 80 } },
      { title: 'DimEmployeeProjectManagerKey', width: { wpx: 80 } },
      { title: 'DimEmployeeSupervisorKey', width: { wpx: 80 } },
      { title: 'DimEmployeeProjectSourceOneKey', width: { wpx: 80 } },
      { title: 'DimEmployeeProjectSourceTwoKey', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
    ];

    tableRows.forEach((data) => {
      export_value.Project.data.push([
        {
          value: data.ProjectId ? data.ProjectId : '',
        },
        { value: data.ProjectName ? data.ProjectName : '' },
        {
          value: data.DimClientKey ? Number(data.DimClientKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimProjectTypeKey ? Number(data.DimProjectTypeKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimProjectSubTypeKey
            ? Number(data.DimProjectSubTypeKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeDirectorKey
            ? Number(data.DimEmployeeDirectorKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeProjectManagerKey
            ? Number(data.DimEmployeeProjectManagerKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeSupervisorKey
            ? Number(data.DimEmployeeSupervisorKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeProjectSourceOneKey
            ? Number(data.DimEmployeeProjectSourceOneKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimEmployeeProjectSourceTwoKey
            ? Number(data.DimEmployeeProjectSourceTwoKey)
            : -1,
          style: { numFmt: '0' },
        },
        { value: data.IsActive ? 'true' : 'false' },
      ]);
    });

    export_value.Client.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    clients &&
      clients.forEach((cur) => {
        export_value.Client.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Project Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    projecttypes &&
      projecttypes.forEach((cur) => {
        export_value['Project Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Project Sub Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    projectsubtypes &&
      projectsubtypes.forEach((cur) => {
        export_value['Project Sub Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value.Employee.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    clients &&
      clients.forEach((cur) => {
        export_value.Employee.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    return export_value;
  }, [tableRows, clients, projecttypes, projectsubtypes]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="Project" value={exportValue} />
        <IconButton onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={projectColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

ProjectTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  clients: PropTypes.array.isRequired,
  projecttypes: PropTypes.array.isRequired,
  projectsubtypes: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  selectedDates: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  onUpdateDropDownData: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getEmployees: PropTypes.func.isRequired,
  getProjectTypes: PropTypes.func.isRequired,
  getProjectSubTypes: PropTypes.func.isRequired,
};

export default ProjectTable;
